import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  IGeoJson2DGeographicCoordinates,
  IGlobeViewSpecificItemData,
  IPosition,
  IProjectAddress,
  IProjectAddressInput,
  ISitemapItemShapeCoordinates,
  ISitemapSpecificItemData,
  LocationType,
  SitemapItemShapeType,
  UploadingType,
} from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { ALLOWED_SITEMAP_ITEMS_WITHOUT_DATA_OBJECT } from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import CircleShapeCoordinates from '~/client/src/shared/components/SitemapHelpers/models/CircleShapeCoordinates'
import GlobeViewCircleProperties from '~/client/src/shared/components/SitemapHelpers/models/GlobeViewCircleProperties'
import { GlobeViewShapeProperties } from '~/client/src/shared/components/SitemapHelpers/models/GlobeViewItemProperties'
import GlobeViewPolyLineProperties from '~/client/src/shared/components/SitemapHelpers/models/GlobeViewPolyLineProperties'
import GlobeViewRectangleProperties from '~/client/src/shared/components/SitemapHelpers/models/GlobeViewRectangleProperties'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import MapViewItemFactory from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemFactory'
import PolyLineShapeCoordinates from '~/client/src/shared/components/SitemapHelpers/models/PolyLineShapeCoordinates'
import RectangleShapeCoordinates from '~/client/src/shared/components/SitemapHelpers/models/RectangleShapeCoordinates'
import SitemapCircleProperties from '~/client/src/shared/components/SitemapHelpers/models/SitemapCircleProperties'
import MapViewItemDrawnPart from '~/client/src/shared/components/SitemapHelpers/models/SitemapItemDrawnPart'
import { SitemapShapeProperties } from '~/client/src/shared/components/SitemapHelpers/models/SitemapItemProperties'
import SitemapPolyLineProperties from '~/client/src/shared/components/SitemapHelpers/models/SitemapPolyLineProperties'
import SitemapRectangleProperties from '~/client/src/shared/components/SitemapHelpers/models/SitemapRectangleProperties'
import type { LocationIconName } from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import { SCALED_IMAGE_CONFIG } from '~/client/src/shared/constants/scaledImage'
import MapViewItemType from '~/client/src/shared/enums/MapViewItemType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import HierarchyNode from '~/client/src/shared/models/HierarchyNode'
import IAccessibleLevelsAttribute from '~/client/src/shared/models/IAccessibleLevelsAttribute'
import Area from '~/client/src/shared/models/LocationObjects/Area'
import Building from '~/client/src/shared/models/LocationObjects/Building'
import Gate from '~/client/src/shared/models/LocationObjects/Gate'
import InteriorDoor from '~/client/src/shared/models/LocationObjects/InteriorDoor'
import InteriorPath from '~/client/src/shared/models/LocationObjects/InteriorPath'
import Level from '~/client/src/shared/models/LocationObjects/Level'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import LocationIntegration, {
  LocationIntegrationType,
} from '~/client/src/shared/models/LocationObjects/LocationIntegration'
import LogisticsObject from '~/client/src/shared/models/LocationObjects/LogisticsObject'
import OffloadingEquipment from '~/client/src/shared/models/LocationObjects/OffloadingEquipment'
import OtherLocation from '~/client/src/shared/models/LocationObjects/OtherLocation'
import ProjectLocation from '~/client/src/shared/models/LocationObjects/ProjectLocation'
import Route from '~/client/src/shared/models/LocationObjects/Route'
import Staging from '~/client/src/shared/models/LocationObjects/Staging'
import VerticalObject from '~/client/src/shared/models/LocationObjects/VerticalObject'
import Zone from '~/client/src/shared/models/LocationObjects/Zone'
import Sitemap from '~/client/src/shared/models/Sitemap'
import SitemapItem, {
  ShapeCoordinates,
} from '~/client/src/shared/models/SitemapItem'
import {
  createdLocationKind,
  creatableLocationKindsOf,
  type ICreatableLocationKinds,
} from '~/client/src/shared/resources/domain/creatableLocationKinds'
import {
  getLocationKind,
  isLocationResourceType,
  type LocationKindValue,
} from '~/client/src/shared/resources/domain/locationKind'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import type ProjectResourcesStore from '~/client/src/shared/stores/domain/ProjectResources.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SyncRestrictionsStore from '~/client/src/shared/stores/domain/SyncRestrictions.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import GlobeViewSetupStore from '~/client/src/shared/stores/GlobeViewSetup.store'
import {
  sortAttributes,
  sortLevels,
} from '~/client/src/shared/utils/sortingFunctions'
import { toast } from '~/client/src/shared/utils/toast'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'
import { areObjectsEqual } from '~/client/src/shared/utils/util'

import { ILevelDisplayInfo } from '../components/PropertiesPanel/BuildingProperties'
import MapViewSetUpStore from '../MapViewSetUp.store'
import {
  createMapViewItemHierarchy,
  type IMapViewItemHierarchy,
} from './mapViewItemHierarchy'
import PendingPlacement from './pendingPlacement'
import { projectMapViewItems } from './projectMapViewItems'
import SitemapsSetupStore from './SitemapsSetup.store'
import { visibleHierarchyNodes } from './visibleHierarchyNodes'

const MAX_IMAGE_WIDTH = 1920
const MAX_IMAGE_HEIGHT = 1080
const PLACEMENT_HINT_TOAST_TIMEOUT = 6000
const NOT_PLACED_TOAST_KEY = 'map-setup-not-placed'
const MIN_POLYLINE_POINTS = 2

const { Circle, Rectangle, Polyline } = SitemapItemShapeType

export const markupTools = 'Markup Tools'
export const maturixStations = 'Maturix Stations'
export const projectOverviewMaps = 'Project Overview Maps'

const ALLOWED_SHAPES_BY_TYPE = {
  [LocationType.OffloadingEquipment]: [Rectangle, Polyline, Circle],
  [LocationType.Route]: [Polyline],
  [LocationType.InteriorPath]: [Polyline],
}

const DEFAULT_ALLOWED_SHAPES = [Rectangle, Polyline]

/**
 * Copies an id the save wrote onto its throwaway copy back to the live record.
 *
 * Only fills a gap - a live record that already has an id is never overwritten, so this
 * cannot repoint an existing row at something else.
 */
const adoptCreatedId = (live: { id?: string }, saved: { id?: string }) => {
  if (live && !live.id && saved?.id) {
    live.id = saved.id
  }
}

const sitemapSaveEvents = [
  e.SAVE_BASEMAP,
  e.SAVE_SITEMAP_ITEM,
  e.SAVE_SITEMAP_IMAGE,
  e.SAVE_GLOBE_IMAGE,
]
const copiedLevelChildren = "Copied level's children"
const copiedSuccessfully = 'Copied Successfully'
const pastedSuccessfully = 'Pasted Successfully'
interface IActionHistory {
  selectedItem: MapViewItemBase
}

export enum ItemsCollapseState {
  collapsed,
  notCollapsed,
  transit,
}

export default class MapViewItemsSetupStore {
  @observable public isSavingAfterChanges: boolean = false
  @observable public searchKey: string = ''
  @observable public isUploading: boolean = false
  @observable public imageFile: File = null
  @observable public imageFileUrl: string = null
  @observable public selectedMapViewItem: MapViewItemBase = null
  @observable public selectedMapViewItemDrawnPart: MapViewItemDrawnPart = null
  // The selection as first shown, for an item the view does not hold and so cannot compare with.
  @observable private selectionBaseline: MapViewItemBase = null

  @observable public isTextStickerCreationActive: boolean = false
  @observable public creatableAttributeType: LocationType = null
  @observable public creatableAttributeIcon: LocationIconName = null
  // Set for a palette entry its icon cannot classify: a ROOT shares its glyph with one of its
  // own leaves, and an Information Point is drawn with a library sign. Null otherwise.
  @observable public creatableAttributeKind: LocationKindValue = null
  // Set when the location is named after its icon rather than its type.
  @observable public isCreatableAttributeNamedByIcon: boolean = false

  @observable public focusedLevel: ILevelDisplayInfo = null
  @observable public copiedShapeProperties:
    | SitemapShapeProperties
    | GlobeViewShapeProperties = null
  @observable public copiedShapeCoordinates: ShapeCoordinates = null
  @observable public copiedItem: MapViewItemBase = null
  @observable public deletableMapViewItem: MapViewItemBase = null
  @observable public mapViewItemForSave: MapViewItemBase = null
  // What the save-confirm dialog interrupted, resumed by whichever button ends it.
  private pendingAfterSaveDialog: () => void = null
  @observable public hoveredNode: HierarchyNode = null

  @observable private hierarchyExpandState = new Map<string, boolean>()
  @observable private isSaving: boolean = false
  @observable public newProjectAddress: IProjectAddressInput = null

  public readonly pendingPlacement: PendingPlacement = new PendingPlacement()

  private actionsHistory: IActionHistory[] = []
  private historyIndex: number = -1

  public constructor(
    private readonly eventsStore: DesktopEventStore,
    private readonly sitemapItemsStore: SitemapItemsStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly syncRestrictionsStore: SyncRestrictionsStore,
    private readonly mapViewSetUpStore: MapViewSetUpStore,
    private readonly fileUploadingStore: FileUploadingStore,
    private readonly locationResourcesStore: Pick<
      ProjectResourcesStore<unknown, unknown>,
      'propertiesStateFor'
    >,
  ) {
    makeObservable(this)
  }

  @computed
  public get hierarchyList(): HierarchyNode[] {
    const nodes: HierarchyNode[] = []
    const markupNodes: HierarchyNode[] = []
    const maturixNodes: HierarchyNode[] = []
    const markupNode = new HierarchyNode(
      this.hierarchyExpandState,
      1,
      null,
      null,
      markupTools,
    )

    const maturixStationsNode = new HierarchyNode(
      this.hierarchyExpandState,
      1,
      null,
      null,
      maturixStations,
    )

    const mapViewItemHierarchy = this.mapViewItemHierarchy
    mapViewItemHierarchy.rootItems.forEach(item => {
      const node = new HierarchyNode(this.hierarchyExpandState, 1, item)
      if (node.item?.dataObject?.is(LocationType.ProjectLocation)) {
        nodes.push(node)
        node.children = this.getNodeChildren(node, mapViewItemHierarchy, nodes)
      } else if (item.dataObject?.is(LocationIntegrationType.MaturixStation)) {
        node.parent = maturixStationsNode
        node.level = 2
        maturixNodes.push(node)
        node.children = this.getNodeChildren(
          node,
          mapViewItemHierarchy,
          maturixNodes,
        )
        maturixStationsNode.children.push(node)
      } else {
        node.parent = markupNode
        node.level = 2
        markupNodes.push(node)
        node.children = this.getNodeChildren(
          node,
          mapViewItemHierarchy,
          markupNodes,
        )
        markupNode.children.push(node)
      }
    })

    const res = [...nodes]
    if (markupNodes.length) {
      res.push(markupNode)
      res.push(...markupNodes)
    }
    if (maturixNodes.length) {
      res.push(maturixStationsNode)
      res.push(...maturixNodes)
    }
    return res
  }

  @computed
  public get visibleHierarchyList(): HierarchyNode[] {
    return visibleHierarchyNodes(this.hierarchyList, this.searchKey)
  }

  public setImage = async (file: File): Promise<void> => {
    this.isUploading = true
    this.imageFile = file
    this.imageFileUrl = await this.getImageUrl(file)
    this.isUploading = false
  }

  public setImageFromBrowse = async event => {
    this.isUploading = true
    const file = event.target.files[0]
    this.imageFile = file
    this.imageFileUrl = await this.getImageUrl(file)
    this.isUploading = false
  }

  @action.bound
  public setSearchKey(key: string): void {
    this.searchKey = key
  }

  @action.bound
  public resetSearchKey(): void {
    this.searchKey = EMPTY_STRING
  }

  public copyLevel = (item?: MapViewItemBase): void => {
    const itemToCopy = item || this.selectedMapViewItem
    if (itemToCopy?.dataObject?.type === LocationType.Level) {
      this.copiedItem = itemToCopy
      toast.success(copiedLevelChildren)
    }
  }

  public pasteLevel = async (item?: MapViewItemBase): Promise<void> => {
    const itemToPaste = item || this.selectedMapViewItem
    if (
      itemToPaste?.dataObject?.type === LocationType.Level &&
      !!this.copiedItem
    ) {
      const { isReady, isFailed, isCreatable } = this.creatableLocationKinds
      if (!isReady) {
        toast.warning(
          isFailed
            ? Localization.translator.locationTypesFailedToLoad
            : Localization.translator.locationTypesStillLoading,
        )
        return
      }

      let isAnySkipped = false
      const isPastable = ({ dataObject }: MapViewItemBase): boolean => {
        const kind = getLocationKind(dataObject)
        const isKept = !kind || isCreatable(kind)
        if (!isKept) isAnySkipped = true
        return isKept
      }
      await this.saveItemChildren(this.copiedItem, itemToPaste, isPastable)

      if (isAnySkipped) {
        toast.warning(Localization.translator.uncreatableLocationTypesSkipped)
      } else {
        toast.success(pastedSuccessfully)
      }
    }
  }

  /** What the active project can create. */
  @computed
  public get creatableLocationKinds(): ICreatableLocationKinds {
    return creatableLocationKindsOf(
      this.locationResourcesStore.propertiesStateFor(
        this.state.activeProject.id,
      ),
    )
  }

  // Only a kind known to be refused: while the Type list is not known, the server decides.
  private refusesLocationKind(kind: LocationKindValue | null): boolean {
    const { isReady, isCreatable } = this.creatableLocationKinds
    if (!kind || !isReady || isCreatable(kind)) {
      return false
    }
    toast.warning(Localization.translator.uncreatableLocationType)
    return true
  }

  /** The kind the armed tool creates, judged as the creation card seeds it. */
  @computed
  public get armedLocationKind(): LocationKindValue | null {
    return createdLocationKind(
      this.creatableAttributeKind,
      this.creatableAttributeIcon,
      this.creatableAttributeType,
    )
  }

  public copy = (): void => {
    if (this.isGlobeMode) {
      this.copiedShapeProperties = this.setGlobeViewShapeProperties(
        this.selectedMapViewItem.sitemapItemProperties.shapeProperties,
      )
      this.copiedShapeCoordinates = this.setGlobeViewShapeCoordinates(
        this.selectedMapViewItem.sitemapItem.shapeCoordinates,
      )
    } else {
      const { iconProperties } = this.selectedMapViewItem.sitemapItemProperties
      const x = -iconProperties?.position?.x || 0
      const y = -iconProperties?.position?.y || 0
      this.copiedShapeProperties = this.setSitemapShapeProperties(
        this.selectedMapViewItem.sitemapItemProperties.shapeProperties,
        x,
        y,
      )
    }

    toast.success(copiedSuccessfully)
  }

  public paste = (): void => {
    if (this.isGlobeMode) {
      this.selectedMapViewItem.globeViewItemProperties.shapeProperties =
        this.setGlobeViewShapeProperties(
          this.copiedShapeProperties as GlobeViewShapeProperties,
        )
      this.selectedMapViewItem.sitemapItem.shapeCoordinates =
        this.setGlobeViewShapeCoordinates(this.copiedShapeCoordinates)
    } else {
      const { iconProperties } = this.selectedMapViewItem.sitemapItemProperties
      const x = iconProperties?.position?.x || 0
      const y = iconProperties?.position?.y || 0
      this.selectedMapViewItem.sitemapItemProperties.shapeProperties =
        this.setSitemapShapeProperties(
          this.copiedShapeProperties as SitemapShapeProperties,
          x,
          y,
        )
    }
    toast.success(pastedSuccessfully)
  }

  @computed
  public get sitemapHierarchyTree(): HierarchyNode[] {
    const hierarchy: HierarchyNode[] = []
    const maturixStationsNode = new HierarchyNode(
      this.hierarchyExpandState,
      0,
      null,
      null,
      maturixStations,
    )

    const mapViewItemHierarchy = this.mapViewItemHierarchy
    mapViewItemHierarchy.rootItems.forEach(item => {
      const node = new HierarchyNode(this.hierarchyExpandState, 1, item)
      const dataObject = node.item?.dataObject
      node.children = this.getNodeChildren(node, mapViewItemHierarchy)

      if (node.item?.dataObject) {
        hierarchy.push(node)
      } else if (dataObject?.is(LocationIntegrationType.MaturixStation)) {
        maturixStationsNode.children.push(node)
      }
    })

    return [...hierarchy, maturixStationsNode]
  }

  public getItemById = (id: string): SitemapItem => {
    return this.sitemapItemsStore.byId.get(id)
  }

  @computed
  public get displayedSitemapItems(): MapViewItemBase[] {
    return this.mapViewItems.filter(
      i => i.isVisibleOnMap && !this.isMapViewItemSelected(i),
    )
  }

  /**
   * Map view items whose resource is allowed on maps (Show in Map on). The setup
   * editor's object tree and its count build from this, so a hidden resource is
   * dropped from the list in one place. `mapViewItems` stays the full set for
   * saves, id lookups, and name-uniqueness checks.
   */
  @computed
  public get shownMapViewItems(): MapViewItemBase[] {
    return this.mapViewItems.filter(item => item.isShownInMap)
  }

  @computed
  private get mapViewItemHierarchy(): IMapViewItemHierarchy<MapViewItemBase> {
    return createMapViewItemHierarchy(this.shownMapViewItems)
  }

  public toggleItemsCollapsingState = (): void => {
    if (this.hierarchyExpandState.size > 0) {
      this.hierarchyExpandState.clear()
    } else {
      this.mapViewItems.forEach(item => {
        this.hierarchyExpandState.set(item.id, true)
      })
      this.hierarchyExpandState.set(markupTools, true)
      this.hierarchyExpandState.set(maturixStations, true)
    }
  }

  public get itemsCollapseState(): ItemsCollapseState {
    switch (true) {
      case this.hierarchyExpandState.size === 0:
        return ItemsCollapseState.notCollapsed
      case this.hierarchyExpandState.size === this.mapViewItems.length + 1:
        return ItemsCollapseState.collapsed
      default:
        return ItemsCollapseState.transit
    }
  }

  public get globeView(): GlobeView {
    return this.globeViewSetupStore.selectedGlobeView
  }

  public getAllowedShapesForType = (
    type: LocationType,
  ): SitemapItemShapeType[] => {
    return ALLOWED_SHAPES_BY_TYPE[type] || DEFAULT_ALLOWED_SHAPES
  }

  @computed
  public get mapViewItems(): MapViewItemBase[] {
    const items = this.persistedMapViewItems
    const selectedItem = this.selectedMapViewItem
    if (
      !selectedItem ||
      selectedItem.id ||
      (!this.globeView && this.isSitemapBlocked)
    ) {
      return items
    }
    return [...items, selectedItem]
  }

  @computed
  private get persistedMapViewItems(): MapViewItemBase[] {
    const globe = this.globeView
    const sitemap = this.sitemapsSetupStore.selectedSitemap
    if (!globe && !sitemap) return []

    return projectMapViewItems({
      mapView: globe
        ? { kind: 'globe', map: globe }
        : { kind: 'sitemap', map: sitemap },
      projectId: this.state.activeProject.id,
      attributes: this.hierarchyAttributes,
      sitemapItems: this.sitemapItemsStore.list,
      displaySources: [
        ...this.sitemapsSetupStore.whiteboards,
        ...this.allGlobeViews,
      ],
      allowedDatalessItemTypes: ALLOWED_SITEMAP_ITEMS_WITHOUT_DATA_OBJECT,
    })
  }

  @computed
  public get selectedSitemapItemAllowedParents(): LocationBase[] {
    if (!this.selectedMapViewItem) {
      return
    }

    const { dataObject } = this.selectedMapViewItem

    let allowedAttributes = this.hierarchyAttributes.filter(dto =>
      isLocationResourceType(dto.type),
    )

    if (dataObject && dataObject?.is(LocationType.ProjectLocation)) {
      allowedAttributes = allowedAttributes.filter(
        a => a.id !== dataObject.id && !a.hasParent,
      )
    }
    return allowedAttributes
  }

  public isMapViewItemSelected = (item: MapViewItemBase): boolean => {
    return this.selectedMapViewItem && this.selectedMapViewItem.id === item.id
  }

  @action.bound
  public async selectMapViewItem(item: MapViewItemBase): Promise<void> {
    if (!item) {
      this.safelyDeselectMapViewItem()
      return
    }
    // Disarm before the save below, which suspends on the network.
    this.disableItemCreation()
    // Unsaved items share a null id, so ids can't tell two of them apart.
    const isAlreadySelected =
      this.selectedMapViewItem === item ||
      (!!item.id && this.isMapViewItemSelected(item))
    if (!isAlreadySelected) {
      if (
        !this.selectedMapViewItem ||
        !this.selectedMapViewItem.isValid(this.isGlobeMode)
      ) {
        this.deselectMapViewItem()
      } else if (
        this.confirmBeforeLeavingSelection(() => this.selectMapViewItem(item))
      ) {
        return
      } else if (!(await this.saveSelectedMapViewItem())) {
        // A failed save keeps its item selected on purpose; replacing the
        // selection here would throw away the work it just failed to persist.
        if (this.selectedMapViewItem) {
          return
        }
      }
    }

    runInAction(() => {
      this.selectedMapViewItem = item.copy()
    })
    if (
      this.selectedMapViewItem.sitemapItem.shapeCoordinates &&
      this.selectedMapViewItem.globeViewItemProperties.shapeProperties?.type ===
        SitemapItemShapeType.Polyline
    ) {
      runInAction(() => {
        // eslint-disable-next-line @typescript-eslint/no-extra-semi
        ;(
          this.selectedMapViewItem.globeViewItemProperties
            .shapeProperties as GlobeViewPolyLineProperties
        ).isClosed =
          this.selectedMapViewItem.sitemapItem.shapeCoordinates?.isClosed
      })
    }
    if (!this.selectedMapViewItem.isDisplayed) {
      this.selectedMapViewItem.fillDataBeforeDisplayingFromSitemaps(
        this.allWhiteboards,
      )
    }
    runInAction(() => {
      this.selectionBaseline = this.selectedMapViewItem.copy()
    })
    this.announceIfUnplaced()
    this.selectSitemapItemDrawnPartIfRequired()
    this.initActionsHistory()
  }

  // Its action acts on the selection, so the toast must not outlive it.
  private announceIfUnplaced(): void {
    if (!this.shouldOfferPlacement) {
      toast.dismiss(NOT_PLACED_TOAST_KEY)
      return
    }
    toast.info(Localization.translator.notPlacedOnMap, {
      key: NOT_PLACED_TOAST_KEY,
      timeout: PLACEMENT_HINT_TOAST_TIMEOUT,
      action: {
        text: Localization.translator.placeOnMap,
        onClick: () => {
          toast.dismiss(NOT_PLACED_TOAST_KEY)
          this.showSelectedMapViewItem()
        },
      },
    })
  }

  public selectSitemapItemDrawnPartIfRequired(): void {
    const {
      id,
      sitemapItem: { type: sitemapItemType },
    } = this.selectedMapViewItem
    const {
      iconProperties,
      labelProperties,
      shapeProperties,
      hasMultipleParts,
    } = this.isGlobeMode
      ? this.selectedMapViewItem.globeViewItemProperties
      : this.selectedMapViewItem.sitemapItemProperties

    if (hasMultipleParts || (id && sitemapItemType === MapViewItemType.Line)) {
      return
    }
    if (iconProperties) {
      this.selectSitemapItemDrawnPart(MapViewItemDrawnPart.Icon)
    }
    if (labelProperties) {
      this.selectSitemapItemDrawnPart(MapViewItemDrawnPart.Label)
    }
    if (shapeProperties) {
      this.selectSitemapItemDrawnPart(MapViewItemDrawnPart.Shape)
    }
  }

  @action.bound
  public selectSitemapItemDrawnPart(part: MapViewItemDrawnPart): void {
    if (
      this.selectedMapViewItem &&
      (this.isGlobeMode
        ? this.selectedMapViewItem.globeViewItemProperties.hasDrawnPart(part)
        : this.selectedMapViewItem.sitemapItemProperties.hasDrawnPart(part))
    ) {
      this.selectedMapViewItemDrawnPart = part
    } else {
      this.deselectMapViewItemDrawnPart()
    }
  }

  @action.bound
  public deselectMapViewItemDrawnPart(): void {
    this.selectedMapViewItemDrawnPart = null
    // ESC on a multi-part item stops here without reaching deselectMapViewItem.
    this.resetMapCursor()
  }

  // Whether the working copy still differs from what the server holds — the only condition the
  // save-confirm dialog exists for.
  @computed
  private get hasUnsavedGlobeChanges(): boolean {
    // The null check carries weight: with nothing selected the lookup below finds nothing, and
    // `!reference` would then report unsaved work that does not exist.
    if (
      !this.selectedMapViewItem ||
      !this.isGlobeMode ||
      this.isUndrawnNewMapViewItem
    ) {
      return false
    }
    const { id } = this.selectedMapViewItem
    // An item the view hides is not on it either, yet merely selecting one changes nothing.
    const reference =
      this.mapViewSetUpStore.mapBoxViewerStore.allGlobeViewItems.find(
        i => i.id === id,
      ) ??
      (id && this.selectionBaseline?.id === id ? this.selectionBaseline : null)
    return (
      !reference ||
      !reference.isEqual(this.selectedMapViewItem, true) ||
      !!this.newProjectAddress
    )
  }

  // Every way of leaving a half-edited item — picking another, arming a creation tool — must ask
  // before persisting it. The caller parks its own continuation on the dialog, so whichever
  // button ends it finishes the job. Returns true when the caller must stop.
  @action
  private confirmBeforeLeavingSelection(resume: () => void): boolean {
    if (!this.hasUnsavedGlobeChanges) {
      return false
    }
    this.mapViewItemForSave = this.selectedMapViewItem
    this.pendingAfterSaveDialog = resume
    return true
  }

  @action.bound
  public safelyDeselectMapViewItem(): void {
    if (this.isGlobeMode && this.isUndrawnNewMapViewItem) {
      this.deselectMapViewItem()
      return
    }
    if (this.hasUnsavedGlobeChanges) {
      this.mapViewItemForSave = this.selectedMapViewItem
    } else {
      this.deselectMapViewItem()
    }
  }

  // Leaving the editing for another surface (the export): disarms the tools, asks about a
  // half-edited item, and goes on once it is saved, discarded or was never changed. An item
  // that can't be saved is dropped, as selecting another item drops it.
  @action.bound
  public leaveSelectionThen(proceed: () => void): void {
    this.disableItemCreation()
    const isSavable = !!this.selectedMapViewItem?.isValid(this.isGlobeMode)
    if (isSavable && this.confirmBeforeLeavingSelection(proceed)) {
      return
    }
    this.deselectMapViewItem()
    proceed()
  }

  // The armed drawing tool holds a data-less polyline whose isValid() is a
  // hardcoded true, so only its geometry can say whether anything was drawn.
  private get isUndrawnNewMapViewItem(): boolean {
    if (
      !this.selectedMapViewItem ||
      this.selectedMapViewItem.id ||
      !this.selectedMapViewItem.isDataLess
    ) {
      return false
    }
    const { shapeCoordinates } = this.selectedMapViewItem.sitemapItem
    const shapeProperties = this.selectedMapViewItem.sitemapItemProperties
      .shapeProperties as SitemapPolyLineProperties
    const isPolyline =
      shapeCoordinates?.type === SitemapItemShapeType.Polyline ||
      shapeProperties?.type === SitemapItemShapeType.Polyline
    // A single vertex can't render either — the disabled real check is > 1.
    return (
      isPolyline &&
      (shapeCoordinates?.coordinates?.length || 0) < MIN_POLYLINE_POINTS &&
      (shapeProperties?.points?.length || 0) < MIN_POLYLINE_POINTS
    )
  }

  @computed
  public get isSitemapBlocked(): boolean {
    return this.selectedMapViewItem && (this.isSaving || this.isSitemapUpdating)
  }

  public get isGlobeMode(): boolean {
    return this.mapViewSetUpStore.isGlobeMode
  }

  public get mapBearing(): number {
    return this.mapViewSetUpStore.mapBoxViewerStore?.viewport?.bearing || 0
  }

  // Returns whether the save succeeded so callers can keep their own editor
  // open on failure (the panel already keeps this store's selection open).
  public saveSelectedMapViewItem = async (
    shouldKeepSelected?: boolean,
    shouldAddToMap?: boolean,
    skipDataObjectSave?: boolean,
  ): Promise<boolean> => {
    if (
      !this.selectedMapViewItem ||
      !this.selectedMapViewItem.isValid(this.isGlobeMode) ||
      this.isUndrawnNewMapViewItem
    ) {
      this.deselectMapViewItem()
      return false
    }
    this.setSaving(true)

    try {
      const objToUpdate = this.selectedMapViewItem.dataObject?.copy()
      const isLvlParentChanged = this.isLevelParentChanged(objToUpdate)

      const itemToSave = this.selectedMapViewItem.copy()

      await this.saveLinkedProjectLocationAddress(itemToSave)
      const { ok } = await this.saveMapViewItem(
        itemToSave,
        shouldAddToMap,
        skipDataObjectSave,
      )

      if (isLvlParentChanged) {
        await this.updateAccessibleLevelsByLevel(objToUpdate)
      }

      if (ok) {
        if (shouldKeepSelected && itemToSave.dataObject?.id) {
          // Re-select the just-saved item: updateIfChanged wrote its new backend
          // id onto the dataObject in place, so a card keyed on the selection
          // re-renders in edit mode instead of the stale, unsaved drawn item.
          // (Only the maps resource card keeps the selection through a save.)
          runInAction(() => {
            this.selectedMapViewItem = itemToSave
            this.selectionBaseline = itemToSave.copy()
          })
          this.initActionsHistory()
        } else {
          if (shouldKeepSelected) {
            // Should not happen: the save succeeded but wrote no backend id.
            // Deselect (the card closes) rather than keep a stale item, and log
            // it so an id write-back regression is debuggable.
            console.warn(
              'saveSelectedMapViewItem: kept-selected save produced no id',
            )
          }
          this.deselectMapViewItem()
        }
      }

      // On save failure (ok === false) we fall through without deselecting, so
      // the user keeps their working context; the global REQUEST_ERROR toast has
      // already surfaced the failure.
      //
      // Both ids are carried back even then. The whole save runs on a deep copy, so a
      // partial failure leaves every id written so far on the throwaway copy while the live
      // selection keeps its nulls, and the retry re-creates whatever it thinks is missing.
      // There are two such points, not one: saveMapViewItem can fail after writing the
      // location but before the sitemap item, and again after writing the sitemap item when
      // adding it to the globe fails. Adopting each id that exists resumes the retry from
      // what is actually persisted, so it writes only the step that did not land.
      if (!ok) {
        runInAction(() => {
          adoptCreatedId(
            this.selectedMapViewItem?.dataObject,
            itemToSave.dataObject,
          )
          adoptCreatedId(
            this.selectedMapViewItem?.sitemapItem,
            itemToSave.sitemapItem,
          )
        })
      }

      return ok
    } finally {
      this.setSaving(false)
    }
  }

  @computed
  public get isSitemapUpdating(): boolean {
    return sitemapSaveEvents.some(ev =>
      this.eventsStore.appState.loading.get(ev),
    )
  }

  public saveMapViewItem = async (
    item: MapViewItemBase,
    shouldAddToMap?: boolean,
    skipDataObjectSave?: boolean,
  ): Promise<{ ok: boolean; isUpdated: boolean }> => {
    if (
      !skipDataObjectSave &&
      item.dataObject &&
      !item.dataObject.id &&
      this.refusesLocationKind(getLocationKind(item.dataObject))
    ) {
      return { ok: false, isUpdated: false }
    }
    this.mapViewSetUpStore.showLoader()

    try {
      // The maps resource card's EDIT save already persists the equipment record
      // via its own resource + controls mutations. Re-saving the location DTO
      // here would write the stale MobX control fields (permitted companies,
      // operating intervals, ...) back over the just-saved controls — so skip it
      // and save only the sitemap placement below. The parent-change restriction
      // sync is a separate side effect of saveDataObjectIfChanged, so run it
      // explicitly when skipping so moving an equipment's parent still recomputes
      // its hierarchy restrictions.
      let dataObject = item.dataObject
      let isDataObjectUpdated = false
      if (!skipDataObjectSave) {
        const saved = await this.saveDataObjectIfChanged(item)
        dataObject = saved.item
        isDataObjectUpdated = saved.isUpdated
      } else {
        this.syncParentRestrictionsIfChanged(item)
      }

      if (dataObject) {
        item.sitemapItem.assign(dataObject)
      }

      const { id: sitemapItemId, isUpdated: isSitemapItemUpdated } =
        await this.saveMapViewItemRelatedData(item)

      if (!sitemapItemId) {
        // Sitemap-item save failed in non-throwing mode. Skip every
        // downstream mutation — we must not overwrite item.sitemapItem.id
        // with null, add the item to the globe/whiteboard, or let the
        // editor report success. The global REQUEST_ERROR toast has
        // already surfaced the failure. Returning ok=false signals to
        // callers like saveSelectedMapViewItem to keep the selection.
        return { ok: false, isUpdated: isDataObjectUpdated }
      }

      item.sitemapItem.id = sitemapItemId

      // Snapshot pre-optimistic display data so we can revert
      // updateMapViewSpecificItem if the follow-up add-to-map save
      // fails. The saveGlobeViewItems rollback only reverts its own
      // inner write — this snapshot covers the outer write that
      // happens before the add-to-map call.
      const isGlobeMode = this.mapViewSetUpStore.isGlobeMode
      const displayDataSnapshot = this.snapshotDisplayDataForItem(
        item.sitemapItem.id,
        isGlobeMode,
      )
      const isSitemapSpecificItemUpdated = this.updateMapViewSpecificItem(item)

      if (isSitemapSpecificItemUpdated && shouldAddToMap) {
        if (isGlobeMode) {
          const savedId = await this.globeViewSetupStore.addItemToGlobeView(
            item.sitemapItem.id,
            item,
          )
          if (!savedId) {
            this.restoreDisplayDataForItem(
              item.sitemapItem.id,
              isGlobeMode,
              displayDataSnapshot,
            )
            return {
              ok: false,
              isUpdated: isDataObjectUpdated || isSitemapItemUpdated,
            }
          }
        } else {
          await this.sitemapsSetupStore.addItemToSitemap(
            item.sitemapItem.id,
            item,
          )
        }
      }

      return {
        ok: true,
        isUpdated:
          isDataObjectUpdated ||
          isSitemapItemUpdated ||
          isSitemapSpecificItemUpdated,
      }
    } finally {
      this.mapViewSetUpStore.hideLoader()
    }
  }

  private snapshotDisplayDataForItem(
    sitemapItemId: string,
    isGlobeMode: boolean,
  ): IGlobeViewSpecificItemData | ISitemapSpecificItemData | null {
    if (isGlobeMode) {
      const globe = this.globe
      if (globe?.hasDisplayData(sitemapItemId)) {
        return { ...globe.getItemDisplayData(sitemapItemId) }
      }
      return null
    }
    const whiteboard = this.whiteboard
    if (!whiteboard?.hasDisplayData(sitemapItemId)) {
      return null
    }
    return { ...whiteboard.getItemDisplayData(sitemapItemId) }
  }

  private restoreDisplayDataForItem(
    sitemapItemId: string,
    isGlobeMode: boolean,
    snapshot: IGlobeViewSpecificItemData | ISitemapSpecificItemData | null,
  ): void {
    runInAction(() => {
      if (isGlobeMode) {
        const globe = this.globe
        if (!globe) {
          return
        }
        if (snapshot) {
          globe.setItemDisplayData(
            sitemapItemId,
            snapshot as IGlobeViewSpecificItemData,
          )
          return
        }
        const idx = globe.items?.findIndex(
          x => x.sitemapItemId === sitemapItemId,
        )
        if (idx != null && idx !== -1) {
          globe.items.splice(idx, 1)
        }
        return
      }
      const whiteboard = this.whiteboard
      if (!whiteboard) {
        return
      }
      if (snapshot) {
        whiteboard.setItemDisplayData(
          sitemapItemId,
          snapshot as ISitemapSpecificItemData,
        )
      } else {
        whiteboard.deleteItemDisplayData(sitemapItemId)
      }
    })
  }

  public async saveDataObjectIfChanged(item: MapViewItemBase): Promise<any> {
    const { dataObject } = item
    if (!dataObject) {
      return { item: null, isUpdated: false }
    }
    const existingObject = this.hierarchyAttributes.find(i => i.id === item.id)
    const isParentChanged = !areObjectsEqual(
      existingObject?.parent || {},
      dataObject.parent || {},
    )
    let res
    switch (dataObject.type) {
      case LocationType.Building:
        res = await this.locationAttributesStore.buildingsStore.updateIfChanged(
          dataObject as Building,
        )
        break
      case LocationType.Zone:
        res = await this.locationAttributesStore.zonesStore.updateIfChanged(
          dataObject as Zone,
        )
        break
      case LocationType.Gate:
        res = await this.locationAttributesStore.gatesStore.updateIfChanged(
          dataObject as Gate,
        )
        break
      case LocationType.Route:
        res = await this.locationAttributesStore.routesStore.updateIfChanged(
          dataObject as Route,
        )
        break
      case LocationType.OffloadingEquipment:
        res =
          await this.locationAttributesStore.offloadingEquipmentsStore.updateIfChanged(
            dataObject as OffloadingEquipment,
          )
        break
      case LocationType.LogisticsObject:
        res =
          await this.locationAttributesStore.logisticsObjectsStore.updateIfChanged(
            dataObject as LogisticsObject,
          )
        break
      case LocationType.Integration:
        res =
          await this.locationAttributesStore.locationIntegrationsStore.updateIfChanged(
            dataObject as LocationIntegration,
          )
        break
      case LocationType.VerticalObject:
        res =
          await this.locationAttributesStore.verticalObjectsStore.updateIfChanged(
            dataObject as VerticalObject,
          )
        break
      case LocationType.Level:
        res = await this.locationAttributesStore.levelsStore.updateIfChanged(
          dataObject as Level,
        )
        break
      case LocationType.Area:
        res = await this.locationAttributesStore.areasStore.updateIfChanged(
          dataObject as Area,
        )
        break
      case LocationType.Staging:
        res = await this.locationAttributesStore.stagingsStore.updateIfChanged(
          dataObject as Staging,
        )
        break
      case LocationType.InteriorDoor:
        res =
          await this.locationAttributesStore.interiorDoorsStore.updateIfChanged(
            dataObject as InteriorDoor,
          )
        break
      case LocationType.InteriorPath:
        res =
          await this.locationAttributesStore.interiorPathsStore.updateIfChanged(
            dataObject as InteriorPath,
          )
        break
      case LocationType.ProjectLocation:
        res =
          await this.locationAttributesStore.projectLocationsStore.updateIfChanged(
            dataObject as ProjectLocation,
          )
        break
      // The catch-all bucket (STX1-597): its rows report the collapsed type.
      case LocationType.Location:
        res =
          await this.locationAttributesStore.otherLocationsStore.updateIfChanged(
            dataObject as OtherLocation,
          )
        break
    }

    if (isParentChanged) {
      this.syncRestrictionsStore.updateRestrictionsForItem(dataObject)
    }
    return res
  }

  // The parent-change restriction sync that saveDataObjectIfChanged runs after a
  // location save. Extracted so callers that skip the location DTO save (the maps
  // resource card, which persists the location via its own resource + controls
  // mutations) can still recompute parent-hierarchy restrictions when the parent
  // moved. Compares the committed store parent against the working item's parent,
  // matching saveDataObjectIfChanged's own check.
  public syncParentRestrictionsIfChanged(item: MapViewItemBase): void {
    const { dataObject } = item
    if (!dataObject) {
      return
    }
    const existingObject = this.hierarchyAttributes.find(i => i.id === item.id)
    const isParentChanged = !areObjectsEqual(
      existingObject?.parent || {},
      dataObject.parent || {},
    )
    if (isParentChanged) {
      this.syncRestrictionsStore.updateRestrictionsForItem(dataObject)
    }
  }

  public async saveMapViewItemRelatedData(
    item: MapViewItemBase,
    { throwOnError = false }: { throwOnError?: boolean } = {},
  ): Promise<{ id: string | null; isUpdated: boolean }> {
    const { sitemapItem } = item

    const existingItem = this.sitemapItemsStore.byId.get(sitemapItem.id)

    if (existingItem && existingItem.isEqual(sitemapItem)) {
      return {
        id: sitemapItem.id,
        isUpdated: false,
      }
    }

    const id = await this.saveItem(sitemapItem, { throwOnError })
    if (!id) {
      // Non-throwing mode: saveItem resolved null because the server
      // rejected the save. Bail out before applyOptimisticSave so we
      // don't pollute sitemapItemsStore with a null-id snapshot that
      // later leaks into permit placement / inherited-coord resolution.
      // Callers key on the null id to skip downstream map mutations.
      return { id: null, isUpdated: false }
    }
    this.sitemapItemsStore.applyOptimisticSave(sitemapItem, id)

    return { id, isUpdated: true }
  }

  public updateMapViewSpecificItem(item: MapViewItemBase): boolean {
    const { sitemapItem } = item
    const { isGlobeMode } = this.mapViewSetUpStore
    const displayData = isGlobeMode
      ? this.globe?.displayDataMap[sitemapItem.id]?.item ||
        ({} as IGlobeViewSpecificItemData)
      : this.whiteboard.getItemDisplayData(sitemapItem.id)

    if (!item.isDisplayDataEqual(displayData, isGlobeMode)) {
      const updatedData = item.getDisplayData(isGlobeMode)

      if (this.mapViewSetUpStore.isGlobeMode) {
        this.globe.setItemDisplayData(sitemapItem.id, {
          ...updatedData,
          id: displayData.id,
          globeViewId:
            (displayData as IGlobeViewSpecificItemData)?.globeViewId ||
            this.globe.id,
          projectId: displayData?.projectId || this.globe.projectId,
        })
      } else {
        this.whiteboard.setItemDisplayData(sitemapItem.id, {
          ...updatedData,
          id: displayData.id,
          sitemapId:
            (displayData as ISitemapSpecificItemData)?.sitemapId ||
            this.whiteboard.id,
          projectId: displayData?.projectId || this.whiteboard.projectId,
        })
      }
      return true
    }

    return false
  }

  @action.bound
  public createDataLessItem(type: MapViewItemType): void {
    const item = MapViewItemFactory.createDataLessItem(
      type,
      this.state.activeProject.id,
      this.mapViewItems.map(i => i.name),
    )
    this.selectMapViewItem(item)
  }

  @action.bound
  public enableCreatingAttribute(
    type: LocationType,
    iconName?: LocationIconName,
    kind?: LocationKindValue,
    isNamedByIcon?: boolean,
  ): void {
    if (
      this.confirmBeforeLeavingSelection(() =>
        this.enableCreatingAttribute(type, iconName, kind, isNamedByIcon),
      )
    ) {
      return
    }
    this.saveSelectedMapViewItem()
    this.disableItemCreation()
    this.creatableAttributeType = type
    this.creatableAttributeIcon = iconName
    this.creatableAttributeKind = kind ?? null
    this.isCreatableAttributeNamedByIcon = !!isNamedByIcon
  }

  @action.bound
  public createAttributeFromTilesetPolyshape(
    type: LocationType,
    iconName?: LocationIconName,
    kind?: LocationKindValue,
  ): void {
    if (this.refusesLocationKind(createdLocationKind(kind, iconName, type))) {
      return
    }
    const { tilesetsControlStore } = this.mapViewSetUpStore.mapBoxViewerStore
    const coordinates =
      tilesetsControlStore.selectedTilesetFeatureCoordinatesCenter
    const isClosed = tilesetsControlStore.selectedTilesetFeatureIsClosed

    const color =
      type === LocationType.Building &&
      this.locationAttributesStore.buildingsStore.colorForNewItem

    const shapeCoordinates: ISitemapItemShapeCoordinates = {
      type: SitemapItemShapeType.Polyline,
      parts: tilesetsControlStore.selectedTilesetFeatureShapeParts,
      isClosed,
    }

    const item = MapViewItemFactory.createDataItem(
      type,
      iconName,
      this.state.activeProject.id,
      this.mapViewItems.map(i => i.name),
      null,
      color,
      coordinates,
      shapeCoordinates,
      kind,
    )
    item.isDisplayed = true
    item.globeViewItemProperties.shapeProperties =
      new GlobeViewPolyLineProperties(
        null,
        item.color,
        item.color,
        null,
        isClosed,
        null,
        true,
      )
    item.setParent({
      parentId: this.locationAttributesStore.projectLocation?.id,
    })
    tilesetsControlStore.resetTilesetFeaturesSelection()
    this.selectMapViewItem(item)
  }

  @action.bound
  public enableCreatingText(): void {
    if (this.confirmBeforeLeavingSelection(this.enableCreatingText)) {
      return
    }
    this.saveSelectedMapViewItem()
    this.disableItemCreation()
    this.isTextStickerCreationActive = true
  }

  @action.bound
  private disableCreatingAttribute(): void {
    this.creatableAttributeType = null
    this.creatableAttributeIcon = null
    this.creatableAttributeKind = null
    this.isCreatableAttributeNamedByIcon = false
  }

  // The pallet's tools are mutually exclusive but held in separate state, so
  // every arming path must disarm the others through here.
  @action.bound
  public disableItemCreation(): void {
    this.isTextStickerCreationActive = false
    this.disableCreatingAttribute()
  }

  @action.bound
  public createAttributeInPosition(position: IPosition): void {
    if (this.isTextStickerCreationActive) {
      this.createTextBoxEditor(position)
      return
    }

    if (!this.creatableAttributeType) {
      return
    }
    if (this.refusesLocationKind(this.armedLocationKind)) {
      this.disableItemCreation()
      return
    }

    const color =
      this.creatableAttributeType === LocationType.Building &&
      this.locationAttributesStore.buildingsStore.colorForNewItem

    const coordinates =
      this.mapViewSetUpStore.mapBoxViewerStore.getItemCoordinates(
        position.x,
        position.y,
      )

    const item = MapViewItemFactory.createDataItem(
      this.creatableAttributeType,
      this.creatableAttributeIcon,
      this.state.activeProject.id,
      this.mapViewItems.map(i => i.name),
      position,
      color,
      coordinates,
      undefined,
      this.creatableAttributeKind,
      this.isCreatableAttributeNamedByIcon,
    )
    item.setParent({
      parentId: this.locationAttributesStore.projectLocation?.id,
    })
    item.isDisplayed = true

    this.selectMapViewItem(item)
  }

  public get deletableSitemapItemCaption(): string {
    if (!this.deletableMapViewItem) {
      return
    }
    const { dataObject } = this.deletableMapViewItem
    if (dataObject) {
      return dataObject.getFieldName(this.state)
    }
    return this.deletableMapViewItem.typeCaption
  }

  public get mapViewItemForSaveCaption(): string {
    if (!this.mapViewItemForSave) {
      return
    }
    const { dataObject } = this.mapViewItemForSave
    if (dataObject) {
      return dataObject.getFieldName(this.state)
    }
    return this.mapViewItemForSave.typeCaption
  }

  @action.bound
  public showDeleteConfirmationDialog(item: MapViewItemBase): void {
    if (!item) {
      return
    }
    if (!item.id) {
      return this.deselectMapViewItem()
    }
    this.deletableMapViewItem = item
  }

  @action.bound
  public hideMapViewItemDeleteConfirmDialog(): void {
    this.deletableMapViewItem = null
  }

  @action.bound
  public deselectMapViewItem(): void {
    this.selectedMapViewItem = null
    this.selectionBaseline = null
    toast.dismiss(NOT_PLACED_TOAST_KEY)
    this.mapViewItemForSave = null
    this.pendingAfterSaveDialog = null
    this.newProjectAddress = null
    this.deselectMapViewItemDrawnPart()
    this.clearActionsHistory()
    this.resetMapCursor()
  }

  private resetMapCursor(): void {
    this.mapViewSetUpStore.mapBoxEditorStore?.resetDrawCursor()
  }

  @action.bound
  public hideMapViewItemSaveConfirmDialog(): void {
    // Cancel discards the edit, but it must still honour the click that raised the dialog —
    // dropping the selection on the floor would make the click look ignored.
    const resume = this.pendingAfterSaveDialog
    this.deselectMapViewItem()
    resume?.()
  }

  @action.bound
  public async applyMapViewItemSaveConfirmDialog(): Promise<void> {
    this.isSavingAfterChanges = true

    try {
      const itemToSave = this.mapViewItemForSave
      const resume = this.pendingAfterSaveDialog

      await this.saveLinkedProjectLocationAddress(itemToSave)
      const { ok } = await this.saveMapViewItem(
        itemToSave,
        this.selectedMapViewItem.isDisplayed,
      )
      if (ok) {
        this.deselectMapViewItem()
        resume?.()
      }
    } finally {
      this.isSavingAfterChanges = false
    }
  }

  @action.bound
  public async applyMapViewItemDeleteConfirmDialog(): Promise<void> {
    if (!this.deletableMapViewItem?.id) {
      return
    }

    const item = this.deletableMapViewItem

    this.hideMapViewItemDeleteConfirmDialog()

    this.mapViewSetUpStore.showLoader()

    try {
      if (this.selectedMapViewItem && this.selectedMapViewItem.id === item.id) {
        this.deselectMapViewItem()
      }

      if (item.dataObject) {
        await this.deleteDataObject(item.dataObject)
      }

      if (item.sitemapItem.id) {
        await this.removeSitemapItems([item.sitemapItem.id])
      }

      if (
        item?.sitemapItem?.id &&
        this.whiteboard?.isItemDisplayed?.(item.sitemapItem.id)
      ) {
        this.whiteboard.deleteItemDisplayData(item.sitemapItem.id)

        await this.sitemapsSetupStore.saveSitemap(this.whiteboard)
      }
    } finally {
      this.mapViewSetUpStore.hideLoader()
    }
  }

  public async deleteSitemapItemsByIds(ids: string[]): Promise<void> {
    const items = this.mapViewItems.filter(i => ids.includes(i.id))

    if (this.selectedMapViewItem && ids.includes(this.selectedMapViewItem.id)) {
      this.deselectMapViewItem()
    }

    for (const item of items) {
      if (item.dataObject) {
        await this.deleteDataObject(item.dataObject)
      }

      if (this.whiteboard?.isItemDisplayed?.(item.sitemapItem.id)) {
        this.whiteboard.deleteItemDisplayData(item.sitemapItem.id)
      }
    }

    const itemsIdsToDelete = items.map(i => i.sitemapItem?.id).filter(i => i)
    await this.removeSitemapItems(itemsIdsToDelete)

    await this.sitemapsSetupStore.saveSitemap(this.whiteboard)
  }

  public async deleteDataObject(dataObject: LocationBase): Promise<void> {
    switch (dataObject.type) {
      case LocationType.Building:
        return await this.locationAttributesStore.buildingsStore.removeItems([
          dataObject.id,
        ])
      case LocationType.Zone:
        return await this.locationAttributesStore.zonesStore.removeItems([
          dataObject.id,
        ])
      case LocationType.Gate:
        return await this.locationAttributesStore.gatesStore.removeItems([
          dataObject.id,
        ])
      case LocationType.Route:
        return await this.locationAttributesStore.routesStore.removeItems([
          dataObject.id,
        ])
      case LocationType.OffloadingEquipment:
        return await this.locationAttributesStore.offloadingEquipmentsStore.removeItems(
          [dataObject.id],
        )
      case LocationType.LogisticsObject:
        return await this.locationAttributesStore.logisticsObjectsStore.removeItems(
          [dataObject.id],
        )
      case LocationType.Integration:
        return await this.locationAttributesStore.locationIntegrationsStore.removeItems(
          [dataObject.id],
        )
      case LocationType.Staging:
        return await this.locationAttributesStore.stagingsStore.removeItems([
          dataObject.id,
        ])
      case LocationType.InteriorDoor:
        return await this.locationAttributesStore.interiorDoorsStore.removeItems(
          [dataObject.id],
        )
      case LocationType.InteriorPath:
        return await this.locationAttributesStore.interiorPathsStore.removeItems(
          [dataObject.id],
        )
      case LocationType.VerticalObject:
        return await this.locationAttributesStore.verticalObjectsStore.removeItems(
          [dataObject.id],
        )
      case LocationType.Level:
        return await this.locationAttributesStore.levelsStore.removeItems([
          dataObject.id,
        ])
      case LocationType.Area:
        return await this.locationAttributesStore.areasStore.removeItems([
          dataObject.id,
        ])
      case LocationType.Location:
        return await this.locationAttributesStore.otherLocationsStore.removeItems(
          [dataObject.id],
        )
    }
  }

  public async saveDataObject(
    dataObject: LocationBase,
    shouldSkipAdditionalSaveAction?: boolean,
  ): Promise<string> {
    switch (dataObject.type) {
      case LocationType.Building:
        return await this.locationAttributesStore.buildingsStore.saveItem(
          dataObject as Building,
          shouldSkipAdditionalSaveAction,
        )
      case LocationType.Zone:
        return await this.locationAttributesStore.zonesStore.saveItem(
          dataObject as Zone,
        )
      case LocationType.Gate:
        return await this.locationAttributesStore.gatesStore.saveItem(
          dataObject as Gate,
        )
      case LocationType.Route:
        return await this.locationAttributesStore.routesStore.saveItem(
          dataObject as Route,
        )
      case LocationType.OffloadingEquipment:
        return await this.locationAttributesStore.offloadingEquipmentsStore.saveItem(
          dataObject as OffloadingEquipment,
        )
      case LocationType.LogisticsObject:
        return await this.locationAttributesStore.logisticsObjectsStore.saveItem(
          dataObject as LogisticsObject,
        )
      case LocationType.Integration:
        return await this.locationAttributesStore.locationIntegrationsStore.saveItem(
          dataObject as LocationIntegration,
        )
      case LocationType.Staging:
        return await this.locationAttributesStore.stagingsStore.saveItem(
          dataObject as Staging,
        )
      case LocationType.InteriorDoor:
        return await this.locationAttributesStore.interiorDoorsStore.saveItem(
          dataObject as InteriorDoor,
        )
      case LocationType.InteriorPath:
        return await this.locationAttributesStore.interiorPathsStore.saveItem(
          dataObject as InteriorPath,
        )
      case LocationType.VerticalObject:
        return await this.locationAttributesStore.verticalObjectsStore.saveItem(
          dataObject as VerticalObject,
        )
      case LocationType.Level:
        return await this.locationAttributesStore.levelsStore.saveItem(
          dataObject as Level,
        )
      case LocationType.Area:
        return await this.locationAttributesStore.areasStore.saveItem(
          dataObject as Area,
        )
      case LocationType.ProjectLocation:
        return await this.locationAttributesStore.projectLocationsStore.saveItem(
          dataObject as ProjectLocation,
        )
      case LocationType.Location:
        return await this.locationAttributesStore.otherLocationsStore.saveItem(
          dataObject as OtherLocation,
        )
    }
  }

  public sitemapAssignedItems(sitemap: Sitemap): LocationBase[] {
    if (!sitemap) {
      return []
    }
    return this.hierarchyAttributes.filter(dto =>
      dto.isSitemapAssigned(sitemap.id),
    )
  }

  public get selectedSitemapAssignedItems(): LocationBase[] {
    if (!this.whiteboard) {
      return []
    }
    return this.hierarchyAttributes.filter(dto =>
      dto.isSitemapAssigned(this.whiteboard.id),
    )
  }

  public updateGlobeAssignedItems = async (
    newAssignedIds: string[],
    globe?: GlobeView,
  ): Promise<void> => {
    const globeToCheck = globe || this.globe
    if (!globeToCheck) {
      return
    }

    await this.hierarchyAttributes.forEach(item => {
      if (
        item.isGlobeAssigned(globeToCheck.id) &&
        !newAssignedIds.includes(item.id)
      ) {
        item.deassignGlobe(globeToCheck.id)
        this.saveDataObject(item)
      }

      if (
        !item.isGlobeAssigned(globeToCheck.id) &&
        newAssignedIds.includes(item.id)
      ) {
        item.assignGlobe(globeToCheck.id)
        this.saveDataObject(item)
      }
    })
  }

  public updateSitemapAssignedItems = async (
    newAssignedIds: string[],
    sitemap?: Sitemap,
  ): Promise<void> => {
    const sitemapToCheck = sitemap || this.whiteboard
    if (!sitemapToCheck) {
      return
    }

    await this.hierarchyAttributes.forEach(item => {
      if (
        item.isSitemapAssigned(sitemapToCheck.id) &&
        !newAssignedIds.includes(item.id)
      ) {
        item.deassignSitemap(sitemapToCheck.id)
        this.saveDataObject(item)
      }

      if (
        !item.isSitemapAssigned(sitemapToCheck.id) &&
        newAssignedIds.includes(item.id)
      ) {
        item.assignSitemap(sitemapToCheck.id)
        this.saveDataObject(item)
      }
    })
  }

  public isAttributeAssignedToGlobe = (item: MapViewItemBase): boolean => {
    const { firstBuilding } = this.locationAttributesStore.buildingsStore
    const { globe } = this.mapViewSetUpStore.mapViewItemsSetupStore
    if (!item.dataObject || !firstBuilding) {
      return false
    }
    const { type, id } = item.dataObject
    if (type === LocationType.Building && id === firstBuilding.id) {
      return !this.mapViewSetUpStore.globeViewSetupStore.globeToLocation[
        globe.id
      ]
    }
  }

  public isAttributeAssignedToSitemap = (item: MapViewItemBase): boolean => {
    const { firstBuilding } = this.locationAttributesStore.buildingsStore
    const { whiteboard } = this.mapViewSetUpStore.mapViewItemsSetupStore
    if (!item.dataObject || !firstBuilding) {
      return false
    }
    const { type, id } = item.dataObject
    if (type === LocationType.Building && id === firstBuilding.id) {
      return !this.mapViewSetUpStore.sitemapViewsSetupStore.sitemapToLocation[
        whiteboard.id
      ]
    }
  }

  public isAttributeAssigned = (item: MapViewItemBase): boolean => {
    if (this.globe) {
      return this.isAttributeAssignedToGlobe(item)
    } else {
      return this.isAttributeAssignedToSitemap(item)
    }
  }

  public isItemAssigned = (item: MapViewItemBase): boolean => {
    if (this.globe) {
      return this.isItemAssignedToGlobe(item)
    } else {
      return this.isItemAssignedToSitemap(item)
    }
  }

  public isItemAssignedToSitemap = (item: MapViewItemBase): boolean => {
    return (
      item.dataObject && this.isDataObjectAssignedToSitemap(item.dataObject)
    )
  }

  public isItemAssignedToGlobe = (item: MapViewItemBase): boolean => {
    return item.dataObject && this.isDataObjectAssignedToGlobe(item.dataObject)
  }

  public isDataObjectAssignedToSitemap = (
    dataObject: LocationBase,
  ): boolean => {
    return dataObject.isSitemapAssigned(this.whiteboard?.id)
  }

  public isDataObjectAssignedToGlobe = (dataObject: LocationBase): boolean => {
    return dataObject.isGlobeAssigned(this.globe?.id)
  }

  @computed
  public get areThereDisplayedItems(): boolean {
    return !!this.globe.items.filter(i => !i.isHidden).length
  }

  @action.bound
  public toggleAllItemsVisibility(): void {
    if (!this.isUpdatingSitemapLoaderShown) {
      this.toggleItemsVisibility(this.mapViewItems, this.areThereDisplayedItems)
    }
  }

  @action.bound
  public toggleSingleItemVisibility(
    item: MapViewItemBase,
    avoidSaving?: boolean,
  ): void {
    this.toggleItemsVisibility([item], item.isDisplayed, avoidSaving)
  }

  @action.bound
  public async toggleItemsVisibility(
    items: MapViewItemBase[],
    areItemsDisplayed: boolean,
    avoidSaving?: boolean,
  ): Promise<void> {
    const { showLoader, hideLoader } = this.mapViewSetUpStore

    if (!avoidSaving) {
      showLoader()
    }

    try {
      if (areItemsDisplayed) {
        await this.hideItems(items)
      } else {
        await this.displayItems(items)
      }
    } finally {
      hideLoader()
    }
  }

  @computed
  public get hierarchyAttributes(): LocationBase[] {
    return this.locationAttributesStore.getSortedAllAttributes(sortAttributes)
  }

  public addCurrentStateToHistory = (): void => {
    if (!this.selectedMapViewItem) {
      return
    }
    const newActionHistory: IActionHistory = {
      selectedItem: this.selectedMapViewItem.copy(),
    }

    this.actionsHistory.splice(
      ++this.historyIndex,
      this.actionsHistory.length,
      newActionHistory,
    )
  }

  public undoAction = (): void => {
    if (this.actionsHistory[this.historyIndex - 1]) {
      this.historyIndex--
      this.applyStateFromHistory()
    }
  }

  public redoAction = (): void => {
    if (this.actionsHistory[this.historyIndex + 1]) {
      this.historyIndex++
      this.applyStateFromHistory()
    }
  }

  public updateAccessibleLevelsByLevel = async (
    level: LocationBase<Level>,
  ): Promise<void> => {
    if (level?.type !== LocationType.Level || !level.parent?.parentId) {
      return
    }

    // The building found by id rather than trusted from parentType, which can be stale
    // ("Location", or an old kind) for a level that does sit under a building.
    const buildingDto =
      this.locationAttributesStore.buildingsStore.getInstanceById(
        level.parent.parentId,
      )
    if (!buildingDto) {
      return
    }

    await this.addLevelsToAccessibles(buildingDto, [level.id])
  }

  @action.bound
  public resetAccessibleLevels(item: MapViewItemBase): void {
    const { dataObject } = item

    let attribute: VerticalObject | OffloadingEquipment = null
    switch (dataObject?.type) {
      case LocationType.VerticalObject:
        attribute = dataObject as VerticalObject
        break
      case LocationType.OffloadingEquipment:
        attribute = dataObject as OffloadingEquipment
        break
    }

    if (!attribute) {
      return
    }

    attribute.accessibleLevels = this.getAccessibleLevelsByParentId(
      attribute.parent?.parentId,
    )
  }

  public addLevelsToAccessibles = async (
    buildingDto: Building,
    levelIds: string[],
  ): Promise<void> => {
    this.mapViewSetUpStore.showLoader()

    const {
      list: verticalObjects,
      saveItemsWhenNestedNumberOfLevelsChanges: saveMultipleVerticalObjs,
    } = this.locationAttributesStore.verticalObjectsStore
    const {
      list: equipments,
      saveItemsWhenNestedNumberOfLevelsChanges: saveMultipleEquipments,
    } = this.locationAttributesStore.offloadingEquipmentsStore

    try {
      await this.updateObjectAccessibleLevels(
        verticalObjects,
        saveMultipleVerticalObjs,
        buildingDto,
        levelIds,
      )
      await this.updateObjectAccessibleLevels(
        equipments,
        saveMultipleEquipments,
        buildingDto,
        levelIds,
      )
    } finally {
      this.mapViewSetUpStore.hideLoader()
    }
  }

  private async updateObjectAccessibleLevels<
    T extends IAccessibleLevelsAttribute & LocationBase,
  >(
    list: T[],
    saveMany: (elements: T[]) => Promise<string[]>,
    buildingDto: Building,
    levelIds: string[],
  ): Promise<void> {
    const elements = list.filter(vo => vo.isParent(buildingDto))

    const updatedObjects = this.getUpdatedAccessibleLvlsObjs(
      elements,
      buildingDto,
      levelIds,
    )

    await saveMany(updatedObjects)
  }

  private getUpdatedAccessibleLvlsObjs<T extends IAccessibleLevelsAttribute>(
    list: T[],
    buildingDto: Building,
    levelIds: string[],
  ): T[] {
    return list
      .map(element => {
        const currentAccessibleLevels = this.getAccessibleLevelsByParentId(
          buildingDto.id,
          element,
        )
        const idsToAdd = levelIds.filter(
          lvl => !currentAccessibleLevels.includes(lvl),
        )

        if (!idsToAdd.length) {
          return null
        }

        currentAccessibleLevels.push(...idsToAdd)
        element.accessibleLevels = currentAccessibleLevels

        return element
      })
      .filter(el => el)
  }

  public getAccessibleLevelsByParentId = (
    parentId: string,
    attribute?: IAccessibleLevelsAttribute,
  ): string[] => {
    const { list: allLevels } = this.locationAttributesStore.levelsStore
    const building =
      this.locationAttributesStore.buildingsStore.getInstanceById(parentId)

    let levels = allLevels.filter(l => l.isParent(building))

    if (attribute?.accessibleLevels?.length) {
      const filteredLvls = levels.filter(l =>
        attribute.accessibleLevels.includes(l.id),
      )
      levels = filteredLvls.length ? filteredLvls : levels
    }

    return (
      building?.sortLevelsByCustomOrder(levels) ||
      levels.sort((a, b) => sortLevels(b, a))
    ).map(({ id }) => id)
  }

  private isLevelParentChanged = (attribute: LocationBase): boolean => {
    if (attribute?.type !== LocationType.Level) {
      return false
    }

    const existingLevel =
      this.locationAttributesStore.levelsStore.getInstanceById(attribute?.id)

    return (
      existingLevel &&
      attribute.parent?.parentId !== existingLevel.parent?.parentId
    )
  }

  private initActionsHistory(): void {
    if (this.selectedMapViewItem) {
      this.actionsHistory = [
        {
          selectedItem: this.selectedMapViewItem.copy(),
        },
      ]
      this.historyIndex = 0
    }
  }

  private createTextBoxEditor(position: IPosition): void {
    const textItem = MapViewItemFactory.createDataLessItem(
      MapViewItemType.TextBox,
      this.state.activeProject.id,
      this.mapViewItems.map(i => i.name),
    )
    if (this.isGlobeMode) {
      textItem.sitemapItem.coordinates =
        this.mapViewSetUpStore.mapBoxViewerStore.getItemCoordinates(
          position.x,
          position.y,
        )
    } else {
      textItem.sitemapItemProperties.labelProperties.setPosition(position)
    }
    this.selectMapViewItem(textItem)
  }

  private clearActionsHistory = (): void => {
    this.actionsHistory = []
    this.historyIndex = -1
  }

  private applyStateFromHistory = (): void => {
    const state = this.actionsHistory[this.historyIndex]
    const hasDrawnPart = this.isGlobeMode
      ? state.selectedItem.globeViewItemProperties.hasDrawnPart(
          this.selectedMapViewItemDrawnPart,
        )
      : state.selectedItem.sitemapItemProperties.hasDrawnPart(
          this.selectedMapViewItemDrawnPart,
        )

    if (this.selectedMapViewItemDrawnPart && !hasDrawnPart) {
      this.deselectMapViewItemDrawnPart()
    }

    this.selectedMapViewItem = state.selectedItem.copy()
  }

  private getNodeChildren(
    node: HierarchyNode,
    hierarchy: IMapViewItemHierarchy<MapViewItemBase>,
    nodes?: HierarchyNode[],
    ancestors: Set<MapViewItemBase> = new Set(),
  ): HierarchyNode[] {
    if (!node.item || ancestors.has(node.item)) {
      return []
    }

    ancestors.add(node.item)
    const children: HierarchyNode[] = []
    for (const item of hierarchy.childrenOf(node.item)) {
      if (ancestors.has(item)) {
        continue
      }

      const childNode = new HierarchyNode(
        this.hierarchyExpandState,
        node.level + 1,
        item,
        node,
      )
      if (nodes) {
        nodes.push(childNode)
      }
      childNode.children = this.getNodeChildren(
        childNode,
        hierarchy,
        nodes,
        ancestors,
      )
      children.push(childNode)
    }
    ancestors.delete(node.item)
    return children
  }

  private saveItemChildren = async (
    item: MapViewItemBase,
    newParent: MapViewItemBase,
    isPastable: (item: MapViewItemBase) => boolean,
  ): Promise<void[]> => {
    const children = this.mapViewItems
      .filter(i => i.isParent(item) && isPastable(i))
      .map(i => i.copy())
    if (!children.length) {
      return
    }
    return await Promise.all(
      children.map(async child => {
        const initialChild = child.copy()
        child.sitemapItem.setId(null)
        if (child.dataObject) {
          child.dataObject.id = null
        }

        child.setParent({ parentId: newParent.id })
        await this.saveMapViewItem(child)

        await this.saveItemChildren(initialChild, child, isPastable)
      }),
    )
  }

  private get sitemapsSetupStore(): SitemapsSetupStore {
    return this.mapViewSetUpStore.sitemapsSetupStore
  }

  private get globeViewSetupStore(): GlobeViewSetupStore {
    return this.mapViewSetUpStore.globeViewSetupStore
  }

  private get state(): DesktopInitialState {
    return this.eventsStore.appState
  }

  public get whiteboard(): Sitemap {
    return this.sitemapsSetupStore.selectedSitemap || ({} as Sitemap)
  }

  public get globe(): GlobeView {
    return this.globeViewSetupStore.selectedGlobeView
  }

  private get allWhiteboards(): Sitemap[] {
    // Sorting:
    // selected sitemap first
    // then sitemaps with same basemaps
    // then rest
    // Clone first: sort() mutates in place, and whiteboards is a @computed whose
    // cached array is shared with the picker/pill (which expect store order).
    return [...this.sitemapsSetupStore.whiteboards].sort((a, b) => {
      let aVal = 0
      let bVal = 0

      if (a === this.whiteboard) {
        aVal = 2
      } else if (this.whiteboard?.basemapId === a?.basemapId) {
        aVal = 1
      }

      if (b === this.whiteboard) {
        bVal = 2
      } else if (this.whiteboard?.basemapId === b?.basemapId) {
        bVal = 1
      }

      return bVal - aVal
    })
  }

  private get allGlobeViews(): GlobeView[] {
    return this.globeViewSetupStore.allGlobeViews
  }

  private async hideItems(items: MapViewItemBase[]): Promise<void> {
    const selectedId = this.selectedMapViewItem?.id
    const shouldFlipSelected =
      !!selectedId && items.some(i => i.id === selectedId)
    const previousIsDisplayed = shouldFlipSelected
      ? this.selectedMapViewItem.isDisplayed
      : null
    if (shouldFlipSelected) {
      runInAction(() => {
        this.selectedMapViewItem.isDisplayed = false
      })
    }
    let ok = true
    if (this.isGlobeMode) {
      ok = await this.changeGlobeItemDataVisibility(items, false)
    } else {
      await this.changeSitemapItemDataVisibility(items, false)
    }
    if (!ok && shouldFlipSelected) {
      // Save failed — restore the selected copy's flag so PropertiesPanel
      // doesn't read a stale "not displayed" as shouldAddToMap=false on
      // the next save and silently commit a rejected state.
      runInAction(() => {
        if (this.selectedMapViewItem?.id === selectedId) {
          this.selectedMapViewItem.isDisplayed = previousIsDisplayed
        }
      })
    }
  }

  // Showing it routes through pending placement first. A line is drawn, not placed.
  public isUnplacedMapViewItem(item: MapViewItemBase): boolean {
    return (
      this.isGlobeMode &&
      !!item?.id &&
      !item.isDisplayed &&
      !item.sitemapItem?.coordinates &&
      item.sitemapItem?.type !== MapViewItemType.Line
    )
  }

  @computed
  public get shouldOfferPlacement(): boolean {
    const selected = this.selectedMapViewItem
    return (
      this.isUnplacedMapViewItem(selected) &&
      !this.pendingPlacement.queue.includes(selected.id)
    )
  }

  // Showing re-reads the item from the server, so unsaved edits, such as a move made while the
  // view hid it, are put to the save dialog first. Either answer deselects, so the item is picked
  // again once shown.
  @action.bound
  public showSelectedMapViewItem(): void {
    const selected = this.selectedMapViewItem
    if (!selected?.id || selected.isDisplayed) {
      return
    }
    const { id } = selected
    const show = async (): Promise<void> => {
      const item = this.mapViewItems.find(i => i.id === id)
      if (!item) {
        return
      }
      await this.toggleItemsVisibility([item], false)
      if (!this.selectedMapViewItem) {
        this.selectMapViewItem(this.mapViewItems.find(i => i.id === id))
      }
    }
    if (!this.confirmBeforeLeavingSelection(() => void show())) {
      void show()
    }
  }

  private async displayItems(items: MapViewItemBase[]): Promise<void> {
    // Only flip the selected item's isDisplayed eagerly when this call will
    // actually persist the display change. Items that route into pending
    // placement (no coord yet, not a Line) don't persist until
    // confirmPendingPlacement resolves, and that path sets the flag itself
    // on success. Flipping here would leave a stale true on cancel/failure
    // that PropertiesPanel reads as shouldAddToMap on the next save.
    const selectedInBatch = items.find(
      i => !!this.selectedMapViewItem && this.selectedMapViewItem.id === i.id,
    )
    const selectedWillEnterPendingPlacement =
      !!selectedInBatch && this.isUnplacedMapViewItem(selectedInBatch)
    const shouldFlipSelected =
      !!selectedInBatch && !selectedWillEnterPendingPlacement
    const previousIsDisplayed = shouldFlipSelected
      ? this.selectedMapViewItem.isDisplayed
      : null
    if (shouldFlipSelected) {
      runInAction(() => {
        this.selectedMapViewItem.isDisplayed = true
      })
    }
    let ok = true
    if (this.isGlobeMode) {
      ok = await this.changeGlobeItemDataVisibility(items, true)
    } else {
      await this.changeSitemapItemDataVisibility(items, true)
    }
    if (!ok && shouldFlipSelected) {
      runInAction(() => {
        if (this.selectedMapViewItem?.id === selectedInBatch.id) {
          this.selectedMapViewItem.isDisplayed = previousIsDisplayed
        }
      })
    }

    // Placement has not changed the item yet, and re-selecting would drop unsaved edits to it.
    const oldSelectedItem = selectedWillEnterPendingPlacement
      ? null
      : items.find(i => this.isMapViewItemSelected(i))
    if (oldSelectedItem) {
      const updatedItem = this.mapViewItems.find(
        i => i.id === oldSelectedItem.id,
      )
      this.selectMapViewItem(updatedItem)
    }
  }

  @action.bound
  public async confirmPendingPlacement(): Promise<void> {
    const snapshot = this.pendingPlacement.markConfirming()
    if (!snapshot) {
      return
    }
    const { id, coord } = snapshot
    const item = this.mapViewItems.find(i => i.id === id)
    if (!item) {
      this.advancePendingQueue()
      return
    }
    // Mutate a transient copy (mapViewItems produces fresh copies on each
    // recompute) and explicitly persist the coord. The downstream
    // changeGlobeItemDataVisibility only saves the sitemap item when id is
    // missing or the item is a Line — neither applies here, so without this
    // call the new coord would never reach the server.
    item.sitemapItem.coordinates = coord
    try {
      await this.saveMapViewItemRelatedData(item, { throwOnError: true })
      await this.changeGlobeItemDataVisibility([item], true, {
        throwOnError: true,
      })
      // Keep selectedMapViewItem in sync. It still holds the copy from
      // before placement (null coord, isDisplayed=false). Without this sync, a follow-up
      // save path (properties panel, save-confirm dialog, selection
      // switch) would serialize that stale copy and undo the placement.
      if (this.selectedMapViewItem?.id === id) {
        runInAction(() => {
          this.selectedMapViewItem.sitemapItem.coordinates = { ...coord }
          this.selectedMapViewItem.sitemapItem.id = item.sitemapItem.id
          this.selectedMapViewItem.isDisplayed = true
        })
      }
      this.advancePendingQueue()
    } catch {
      // Save failed — leave the item at the head of the queue so the user
      // can retry. Clearing the confirming flag re-enables the toolbar.
      // Error is surfaced via toast; swallow to avoid an unhandled
      // rejection since the click handler doesn't await this.
      this.pendingPlacement.clearConfirming()
      toast.error(Localization.translator.failedToSavePlacement)
    }
  }

  @action.bound
  public cancelPendingPlacement(): void {
    this.advancePendingQueue()
  }

  @action.bound
  public abortPendingPlacement(): void {
    this.pendingPlacement.cancel()
  }

  @action.bound
  public updatePendingPlacementCoord(
    coord: IGeoJson2DGeographicCoordinates,
  ): void {
    this.pendingPlacement.updateCoord(coord)
  }

  private advancePendingQueue(): void {
    // `advance` shifts the queue internally, so the next head is at index 1.
    const nextHeadId = this.pendingPlacement.queue[1]
    const seed = this.resolveSeedForPendingPlacement(nextHeadId)
    this.pendingPlacement.advance(seed.coord)
    if (seed.isInherited) {
      this.mapViewSetUpStore.mapBoxViewerStore.setViewportFromCoordinates(
        seed.coord,
      )
    }
  }

  /**
   * Resolve the starting coord for a pending placement. Prefers the
   * nearest ancestor that has coordinates anywhere in the system
   * (Location lat/lng lives on the shared SitemapItem — visibility on
   * the current globe doesn't matter). When that produces a real
   * inherited coord, callers also fly the viewport there so the dashed
   * pin lands in view. Otherwise falls back to the current viewport
   * center and no fly-to is needed.
   */
  private resolveSeedForPendingPlacement(locationId: string | undefined): {
    coord: IGeoJson2DGeographicCoordinates
    isInherited: boolean
    resolvedLocationId: string | null
  } {
    const { viewport } = this.mapViewSetUpStore.mapBoxViewerStore
    const fallback = {
      coord: { latitude: viewport.latitude, longitude: viewport.longitude },
      isInherited: false,
      resolvedLocationId: null,
    }
    if (!locationId) {
      return fallback
    }
    // includeProject: false — the ProjectLocation's own coord is the
    // project's georeferenced address, not a meaningful "parent" anchor
    // for a top-level Location with no real parent. When a chain has no
    // non-project ancestor with a sitemap-item coord, we'd rather fall
    // back to the current viewport centre (user sees "drop here") than
    // fly across the map to the project origin.
    const resolved =
      this.mapViewSetUpStore.globeViewsStore.resolveLocationCoordinates(
        locationId,
        { includeProject: false },
      )
    if (!resolved?.coordinates) {
      return fallback
    }
    return {
      coord: resolved.coordinates,
      isInherited: true,
      resolvedLocationId: resolved.resolvedLocationId,
    }
  }

  @action.bound
  private async changeGlobeItemDataVisibility(
    items: MapViewItemBase[],
    shouldDisplay: boolean,
    { throwOnError = false }: { throwOnError?: boolean } = {},
  ): Promise<boolean> {
    const { viewport } = this.mapViewSetUpStore.mapBoxViewerStore
    const { id: globeViewId, projectId } =
      this.globeViewSetupStore.selectedGlobeView

    let itemsToProcess = items
    if (shouldDisplay) {
      const needsPlacement = items.filter(i => this.isUnplacedMapViewItem(i))
      if (needsPlacement.length > 0) {
        const ids = needsPlacement.map(i => i.id)
        if (!this.pendingPlacement.id) {
          const seed = this.resolveSeedForPendingPlacement(ids[0])
          this.pendingPlacement.begin(ids, seed.coord)
          if (seed.isInherited) {
            this.mapViewSetUpStore.mapBoxViewerStore.setViewportFromCoordinates(
              seed.coord,
            )
          }
          if (ids.length > 1) {
            toast.success(
              Localization.translator.placingMultipleItems(ids.length),
              { timeout: PLACEMENT_HINT_TOAST_TIMEOUT },
            )
          } else {
            const inheritedName = seed.isInherited
              ? this.locationAttributesStore.hierarchyAllAttributesMap[
                  seed.resolvedLocationId
                ]?.name
              : null
            toast.success(
              inheritedName
                ? Localization.translator.placedOnLocationDragToAdjust(
                    inheritedName,
                  )
                : Localization.translator.placedAtMapCenterDragToAdjust,
            )
          }
        } else {
          this.pendingPlacement.enqueue(ids)
          toast.success(
            Localization.translator.addedToPlacementQueue(
              this.pendingPlacement.remaining,
            ),
            { timeout: PLACEMENT_HINT_TOAST_TIMEOUT },
          )
        }
        toast.dismiss(NOT_PLACED_TOAST_KEY)
        const excluded = new Set(needsPlacement)
        itemsToProcess = items.filter(i => !excluded.has(i))
      }
    }

    // Track failures so we return success=false to the caller even when
    // the aggregate save would otherwise look fine (e.g. 3 items queued,
    // one sitemap-item save fails → the remaining 2 land but the caller
    // still needs to roll back its optimistic UI state for the failed one).
    let hadFailure = false

    const itemsForSave = await Promise.all(
      itemsToProcess
        .filter(i => !!i.id && i.isDisplayed !== shouldDisplay)
        .map(async item => {
          const isPartOfSelectedGlobeView =
            this.globeViewSetupStore.selectedGlobeView.itemsMap[
              item.sitemapItem.id
            ]
          let shouldSaveSitemapItem = false

          if (
            item.sitemapItem.type === MapViewItemType.Line &&
            !item.sitemapItem.coordinates
          ) {
            item.sitemapItem.coordinates = {
              latitude: viewport.latitude,
              longitude: viewport.longitude,
            }
            shouldSaveSitemapItem = true
          }
          if (!item.sitemapItem?.id) {
            shouldSaveSitemapItem = true
          }
          if (
            item.sitemapItem.type === MapViewItemType.Line &&
            !item.sitemapItem.shapeCoordinates
          ) {
            item.sitemapItem.shapeCoordinates = new PolyLineShapeCoordinates(
              [
                {
                  rings: [
                    [
                      {
                        latitude: viewport.latitude,
                        longitude: viewport.longitude,
                      },
                      {
                        latitude: viewport.latitude,
                        longitude: viewport.longitude + 0.01,
                      },
                    ],
                  ],
                },
              ],
              false,
            )
            shouldSaveSitemapItem = true
          }

          if (shouldSaveSitemapItem) {
            const savedId = (
              await this.saveMapViewItemRelatedData(item, { throwOnError })
            ).id
            if (!savedId) {
              // Non-throwing sitemap-item save failed — skip this item so
              // we don't push a null-sitemapItemId payload into the globe
              // save below. Record the failure so the caller can roll back
              // any optimistic UI flags it flipped before invoking us.
              hadFailure = true
              return null
            }
            item.sitemapItem.id = savedId
          }

          if (isPartOfSelectedGlobeView) {
            const itemToUpdate =
              this.globeViewSetupStore.selectedGlobeView.getItemDisplayData(
                item.sitemapItem.id,
              )
            return {
              ...itemToUpdate,
              isHidden: !shouldDisplay,
            }
          } else {
            if (!shouldDisplay) {
              return null
            }
            item.fillDataBeforeDisplayingFromGlobes(
              this.globeViewSetupStore.globeViewsList,
            )
            if (
              item.sitemapItem.shapeCoordinates?.type &&
              item.globeViewItemProperties?.shapeProperties?.type !==
                item.sitemapItem.shapeCoordinates.type
            ) {
              item.updateGlobeViewShape(item.sitemapItem.shapeCoordinates.type)
            }

            const itemToAdd = {
              ...item.getDisplayData(true),
              projectId,
              globeViewId,
              isHidden: !shouldDisplay,
            } as IGlobeViewSpecificItemData

            return itemToAdd
          }
        }),
    ).then(results => results.filter(i => !!i))

    if (itemsForSave?.length) {
      const savedId = await this.globeViewSetupStore.saveGlobeViewItems(
        itemsForSave,
        { throwOnError },
      )
      if (!savedId) {
        hadFailure = true
      }
    }
    return !hadFailure
  }

  @action.bound
  private async changeSitemapItemDataVisibility(
    items: MapViewItemBase[],
    shouldDisplay: boolean,
  ): Promise<void> {
    const { id: sitemapId, projectId } = this.sitemapsSetupStore.selectedSitemap

    const itemsForSave = await Promise.all(
      items
        .filter(i => i.isDisplayed !== shouldDisplay)
        .map(async item => {
          const exists =
            this.sitemapsSetupStore.selectedSitemap.itemsMap[
              item.sitemapItem.id
            ]

          if (exists) {
            const itemToUpdate =
              this.sitemapsSetupStore.selectedSitemap.getItemDisplayData(
                item.sitemapItem.id,
              )
            return {
              ...itemToUpdate,
              isHidden: !shouldDisplay,
            }
          } else {
            item.fillDataBeforeDisplayingFromSitemaps(
              this.sitemapsSetupStore.whiteboards,
            )
            if (!item.sitemapItem?.id) {
              const savedId = (await this.saveMapViewItemRelatedData(item)).id
              if (!savedId) {
                return null
              }
              item.sitemapItem.id = savedId
            }
            const itemToAdd = {
              ...item.getDisplayData(false),
              sitemapId,
              projectId,
              isHidden: !shouldDisplay,
            } as ISitemapSpecificItemData

            return itemToAdd
          }
        }),
    ).then(results => results.filter(i => !!i))

    this.sitemapsSetupStore.saveSitemapItemData(itemsForSave)
  }

  private async saveLinkedProjectLocationAddress(
    item: MapViewItemBase,
  ): Promise<void> {
    if (
      !item.dataObject?.is(LocationType.ProjectLocation) ||
      !(item.dataObject as ProjectLocation).isLinked
    ) {
      return
    }

    const { mapRef } = this.mapViewSetUpStore.mapBoxViewerStore
    const newBounds = mapRef?.getBounds()
    const zoom = mapRef?.getZoom()
    const bearing = mapRef?.getBearing()
    const newProjectAddress = {
      ...this.eventsStore.appState.projectAddress,
      address:
        this.newProjectAddress?.address ||
        this.eventsStore.appState.projectAddress.address,
      center: {
        lat: item.sitemapItem.coordinates.latitude,
        lng: item.sitemapItem.coordinates.longitude,
      },
      bounds: {
        ne: newBounds?.getNorthEast(),
        sw: newBounds?.getSouthWest(),
      },
      zoom,
      bearing,
    } as IProjectAddress
    await this.mapViewSetUpStore.mapBoxViewerStore.saveAddress(
      newProjectAddress,
    )
  }

  private saveItem(
    sitemapItem: SitemapItem,
    { throwOnError = false }: { throwOnError?: boolean } = {},
  ): Promise<string | null> {
    return new Promise<string | null>((resolve, reject) => {
      this.sitemapItemsStore.save(
        sitemapItem,
        id => resolve(id),
        // Default: resolve null so general callers (PropertiesPanel, bulk
        // saves, paste flows) finish their finally/loader teardown and
        // don't emit unhandled rejections. The global REQUEST_ERROR toast
        // already surfaces the failure. Pending-placement opts in to
        // throwOnError so confirmPendingPlacement can trigger rollback.
        error =>
          throwOnError
            ? reject(error ?? new Error('saveSitemapItem failed'))
            : resolve(null),
      )
    })
  }

  private removeSitemapItems(sitemapItemIds: string[]): Promise<void> {
    return new Promise<void>(resolve => {
      if (!sitemapItemIds?.length) {
        return resolve()
      }

      this.sitemapItemsStore.removeMany(sitemapItemIds, resolve)
    })
  }

  private async getImageUrl(sourceFile: File | Blob): Promise<string> {
    const file = await this.getScaledImage(sourceFile)

    const isImage = file.type.startsWith('image/')

    const fileType = isImage ? UploadingType.Image : UploadingType.Pdf
    const [result] = await this.fileUploadingStore.uploadFile(
      file,
      fileType,
      this.state.activeProject.id,
      (file as File).name,
      true,
    )

    if (!result) {
      return
    }

    return result.fileURL
  }

  private async getScaledImage(file: File | Blob): Promise<File | Blob> {
    const isImage = file.type.startsWith('image/')

    if (!isImage) {
      return file
    }

    const img = new Image()
    img.src = URL.createObjectURL(file)

    await new Promise(resolve => {
      img.onload = resolve
    })

    if (img.width <= MAX_IMAGE_WIDTH && img.height <= MAX_IMAGE_HEIGHT) {
      return file
    }
    return this.scaleImage(img)
  }

  private scaleImage(img: HTMLImageElement): Promise<Blob> {
    let { width, height } = img

    if (width > MAX_IMAGE_WIDTH) {
      height *= MAX_IMAGE_WIDTH / width
      width = MAX_IMAGE_WIDTH
    }

    if (height > MAX_IMAGE_HEIGHT) {
      width *= MAX_IMAGE_HEIGHT / height
      height = MAX_IMAGE_HEIGHT
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0, width, height)

    return new Promise<Blob>(resolve => {
      canvas.toBlob(
        resolve,
        SCALED_IMAGE_CONFIG.mimeType,
        SCALED_IMAGE_CONFIG.quality,
      )
    })
  }

  public get isUpdatingSitemapLoaderShown(): boolean {
    return this.mapViewSetUpStore.isUpdatingSitemapLoaderShown
  }

  private setGlobeViewShapeCoordinates = (
    shapeCoordinates: ShapeCoordinates,
  ):
    | PolyLineShapeCoordinates
    | CircleShapeCoordinates
    | RectangleShapeCoordinates => {
    return shapeCoordinates.copy()
  }

  private setGlobeViewShapeProperties = (
    shape: GlobeViewShapeProperties,
  ):
    | GlobeViewPolyLineProperties
    | GlobeViewCircleProperties
    | GlobeViewRectangleProperties => {
    return shape.copy()
  }

  private setSitemapShapeProperties = (
    shape: SitemapShapeProperties,
    x: number,
    y: number,
  ):
    | SitemapPolyLineProperties
    | SitemapCircleProperties
    | SitemapRectangleProperties => {
    switch (shape.type) {
      case SitemapItemShapeType.Circle:
        const circle = shape.copy() as SitemapCircleProperties
        circle.position = {
          x: circle.position.x + x,
          y: circle.position.y + y,
        }
        return circle
      case SitemapItemShapeType.Rectangle:
        const rectangle = shape.copy() as SitemapRectangleProperties
        rectangle.position = {
          x: rectangle.position.x + x,
          y: rectangle.position.y + y,
        }
        return rectangle
      case SitemapItemShapeType.Polyline:
        const polyline = shape.copy() as SitemapPolyLineProperties
        polyline.points = polyline.points.map(point => {
          return {
            x: point.x + x,
            y: point.y + y,
          }
        })
        return polyline
    }
  }

  @action
  private setSaving(isSaving: boolean): void {
    this.isSaving = isSaving
  }
}
